import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, Trash2, ChevronDown, ChevronUp, ShoppingCart } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { CheckCircle2 } from 'lucide-react';
import { useCart } from '../context/CartContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TEMPLES = [
  { value: 'Parashurama Swami Temple, Thiruvallam', label: 'Parashurama Swami Temple, Thiruvallam', available: true },
  { value: 'Varkala Papanasham Temple', label: 'Varkala Papanasham Temple', available: false }
];

const MALAYALAM_NAKSHATRAS = [
  { value: 'unknown', label: "Don't know" }, { value: 'Ashwathi', label: 'Ashwathi (അശ്വതി)' }, { value: 'Bharani', label: 'Bharani (ഭരണി)' },
  { value: 'Karthika', label: 'Karthika (കാർത്തിക)' }, { value: 'Rohini', label: 'Rohini (രോഹിണി)' }, { value: 'Makayiram', label: 'Makayiram (മകയിരം)' },
  { value: 'Thiruvathira', label: 'Thiruvathira (തിരുവാതിര)' }, { value: 'Punartham', label: 'Punartham (പുണർതം)' }, { value: 'Pooyam', label: 'Pooyam (പൂയം)' },
  { value: 'Ayilyam', label: 'Ayilyam (ആയില്യം)' }, { value: 'Makam', label: 'Makam (മകം)' }, { value: 'Pooram', label: 'Pooram (പൂരം)' },
  { value: 'Uthram', label: 'Uthram (ഉത്രം)' }, { value: 'Atham', label: 'Atham (അത്തം)' }, { value: 'Chithira', label: 'Chithira (ചിത്തിര)' },
  { value: 'Chothi', label: 'Chothi (ചോതി)' }, { value: 'Vishakham', label: 'Vishakham (വിശാഖം)' }, { value: 'Anizham', label: 'Anizham (അനിഴം)' },
  { value: 'Thrikketta', label: 'Thrikketta (തൃക്കേട്ട)' }, { value: 'Moolam', label: 'Moolam (മൂലം)' }, { value: 'Pooradam', label: 'Pooradam (പൂരാടം)' },
  { value: 'Uthradam', label: 'Uthradam (ഉത്രാടം)' }, { value: 'Thiruvonam', label: 'Thiruvonam (തിരുവോണം)' }, { value: 'Avittam', label: 'Avittam (അവിറ്റം)' },
  { value: 'Chathayam', label: 'Chathayam (ചതയം)' }, { value: 'Pooruruttathi', label: 'Pooruruttathi (പൂരുരുട്ടാതി)' },
  { value: 'Uthrattathi', label: 'Uthrattathi (ഉത്രട്ടാതി)' }, { value: 'Revathi', label: 'Revathi (രേവതി)' }
];

const POOJA_LABELS = { 'thilahomam': 'Thilahomam', 'ganapathi-homam': 'Ganapathi Homam', 'archana': 'Archana' };
const POOJA_COLORS = { 'thilahomam': '#C2410C', 'ganapathi-homam': '#B45309', 'archana': '#9A3412' };

export default function CartBookingPage() {
  const navigate = useNavigate();
  const { cartItems, removeFromCart, clearCart } = useCart();
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [expandedItem, setExpandedItem] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [bookerName, setBookerName] = useState('');
  const [bookerPlace, setBookerPlace] = useState('');

  // Shared fields
  const [temple, setTemple] = useState('Parashurama Swami Temple, Thiruvallam');
  const [whatsapp, setWhatsapp] = useState('');
  const [paymentOption, setPaymentOption] = useState('pay_early');
  const [deliveryAddress, setDeliveryAddress] = useState({ fullName: '', address: '', city: '', district: '', pinCode: '', phoneNumber: '' });

  // Per-pooja fields: { [poojaId]: { name, deathDate, dontKnowDate, nakshatra, dontKnowNakshatra, birthNakshatra, dakshina, delivery } }
  const [itemForms, setItemForms] = useState({});

  useEffect(() => {
    axios.get(`${API}/settings`).then(res => {
      setSettings(res.data.poojas || {});
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  // Initialize form state for each cart item
  useEffect(() => {
    if (!settings || Object.keys(settings).length === 0) return;
    const forms = {};
    cartItems.forEach(poojaId => {
      const config = settings[poojaId];
      if (!config) return;
      forms[poojaId] = itemForms[poojaId] || {
        name: '',
        deathDate: '',
        dontKnowDate: false,
        nakshatra: 'unknown',
        dontKnowNakshatra: false,
        birthNakshatra: 'unknown',
        dakshina: String(config.default_dakshina || 20),
        delivery: false,
      };
    });
    setItemForms(forms);
    if (cartItems.length > 0 && expandedItem === null) {
      setExpandedItem(cartItems[0]);
    }
  }, [settings, cartItems.length]);

  const updateItemForm = (poojaId, field, value) => {
    setItemForms(prev => ({ ...prev, [poojaId]: { ...prev[poojaId], [field]: value } }));
  };

  const calculateItemSubtotal = (poojaId) => {
    const config = settings[poojaId];
    const form = itemForms[poojaId];
    if (!config || !form) return 0;
    let total = config.temple_fee + config.convenience_fee;
    if (form.delivery) total += config.delivery_charge;
    if (config.has_dakshina && form.dakshina && !isNaN(parseInt(form.dakshina))) total += parseInt(form.dakshina);
    return total;
  };

  const grandTotal = cartItems.reduce((sum, id) => sum + calculateItemSubtotal(id), 0);
  const anyDelivery = cartItems.some(id => itemForms[id]?.delivery);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: '#C2410C' }}></div></div>;

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: '#FAF9F6' }}>
        <div className="text-center space-y-4">
          <ShoppingCart className="mx-auto h-16 w-16" style={{ color: '#D6D3D1' }} />
          <h2 className="text-2xl font-bold" style={{ color: '#1C1917', fontFamily: '"Cormorant Garamond", serif' }}>Your cart is empty</h2>
          <p className="text-sm" style={{ color: '#78716C' }}>Browse our pooja services and add them to your cart</p>
          <Button onClick={() => navigate('/')} className="rounded-xl px-8 py-3 text-white font-semibold" style={{ backgroundColor: '#C2410C' }} data-testid="browse-poojas-btn">Browse Poojas</Button>
        </div>
      </div>
    );
  }

  const handleSubmit = async () => {
    if (!whatsapp || whatsapp.length < 10) { alert('Please enter a valid WhatsApp number'); return; }
    for (const poojaId of cartItems) {
      const form = itemForms[poojaId];
      const config = settings[poojaId];
      if (!form || !form.name) { alert(`Please enter the name for ${POOJA_LABELS[poojaId] || poojaId}`); setExpandedItem(poojaId); return; }
      if (config.has_death_date && !form.dontKnowDate && !form.deathDate) { alert(`Please enter death date for ${POOJA_LABELS[poojaId] || poojaId} or check "I don't know"`); setExpandedItem(poojaId); return; }
    }
    setShowConfirmDialog(true);
  };

  const handleFinalSubmit = async () => {
    if (!bookerName.trim()) { alert('Please enter your name'); return; }
    if (!bookerPlace.trim()) { alert('Please enter your place'); return; }
    if (submitting) return;
    setSubmitting(true);
    setShowConfirmDialog(false);

    const items = cartItems.map(poojaId => {
      const form = itemForms[poojaId];
      const config = settings[poojaId];
      const nakshatraValue = config.has_death_nakshatra ? ((form.dontKnowDate || form.dontKnowNakshatra) ? 'Thiruvonam' : form.nakshatra !== 'unknown' ? form.nakshatra : 'To be confirmed') : '';
      return {
        pooja_type: poojaId, customer_name: form.name,
        death_person_name: config.has_death_date ? form.name : '',
        death_date: config.has_death_date ? (form.dontKnowDate ? 'Not Known' : form.deathDate || '') : '',
        nakshatra: nakshatraValue,
        devotee_name: !config.has_death_date ? form.name : '',
        birth_nakshatra: (poojaId === 'ganapathi-homam' || poojaId === 'archana') ? (form.birthNakshatra !== 'unknown' ? form.birthNakshatra : '') : '',
        delivery: form.delivery,
        dakshina: config.has_dakshina && form.dakshina ? parseInt(form.dakshina) : 0,
        temple_fee: config.temple_fee, convenience_fee: config.convenience_fee,
        delivery_charge: form.delivery ? config.delivery_charge : 0,
        subtotal: calculateItemSubtotal(poojaId),
      };
    });

    try {
      const response = await axios.post(`${API}/cart-booking`, {
        items, temple, whatsapp,
        delivery_address: anyDelivery ? deliveryAddress : null,
        total: grandTotal, payment_option: paymentOption,
        booker_name: bookerName.trim(), booker_place: bookerPlace.trim(),
        traffic_source: sessionStorage.getItem('prayan_source') || 'direct',
      });
      if (response.data.success) { clearCart(); navigate(`/payment/${response.data.booking_id}`); }
    } catch { alert('Booking failed. Please try again.'); } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FAF9F6' }}>
      {/* Top Bar */}
      <div className="sticky top-0 z-50 bg-white shadow-md">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/')} data-testid="back-home-btn"><ArrowLeft className="h-5 w-5" /></Button>
            <div>
              <h1 className="text-lg font-bold" style={{ color: '#1C1917', fontFamily: '"Cormorant Garamond", serif' }}>Your Cart</h1>
              <p className="text-xs" style={{ color: '#78716C' }}>{cartItems.length} pooja{cartItems.length > 1 ? 's' : ''} selected</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5" style={{ color: '#C2410C' }} />
            <span className="text-sm font-bold" style={{ color: '#C2410C' }}>{cartItems.length}</span>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 pb-32">
        {/* Temple Selection */}
        <div className="bg-white shadow-lg rounded-2xl p-5 mb-4">
          <Label className="text-xs font-medium uppercase tracking-widest mb-2 block" style={{ color: '#57534E' }}>Select Temple *</Label>
          <Select value={temple} onValueChange={setTemple}>
            <SelectTrigger className="w-full rounded-xl p-4 text-base border-stone-200" data-testid="cart-temple-select"><SelectValue /></SelectTrigger>
            <SelectContent>{TEMPLES.map(tp => <SelectItem key={tp.value} value={tp.value} disabled={!tp.available}>{tp.label} {!tp.available && '(N/A)'}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        {/* Pooja Items */}
        <div className="space-y-3 mb-4">
          {cartItems.map((poojaId) => {
            const config = settings[poojaId];
            const form = itemForms[poojaId];
            if (!config || !form) return null;
            const isExpanded = expandedItem === poojaId;
            const color = POOJA_COLORS[poojaId] || '#92400E';

            return (
              <div key={poojaId} className="bg-white shadow-lg rounded-2xl overflow-hidden" data-testid={`cart-item-${poojaId}`}>
                {/* Item Header */}
                <div className="flex items-center justify-between p-4 cursor-pointer" onClick={() => setExpandedItem(isExpanded ? null : poojaId)} style={{ borderLeft: `4px solid ${color}` }}>
                  <div className="flex items-center gap-3 flex-1">
                    <div>
                      <h3 className="font-bold text-base" style={{ color: '#1C1917' }}>{POOJA_LABELS[poojaId] || poojaId}</h3>
                      <p className="text-xs" style={{ color: '#78716C' }}>{form.name || 'Fill details below'} &middot; ₹{calculateItemSubtotal(poojaId)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); removeFromCart(poojaId); }} className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1 h-8 w-8" data-testid={`remove-${poojaId}-btn`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                    {isExpanded ? <ChevronUp className="h-5 w-5" style={{ color: '#78716C' }} /> : <ChevronDown className="h-5 w-5" style={{ color: '#78716C' }} />}
                  </div>
                </div>

                {/* Expanded Form */}
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }} className="overflow-hidden">
                      <div className="px-4 pb-5 space-y-4 border-t" style={{ borderColor: '#F5F5F4' }}>
                        {/* Name */}
                        <div className="space-y-2 pt-4">
                          <Label className="text-xs font-medium uppercase tracking-widest" style={{ color: '#57534E' }}>{config.has_death_date ? 'Death Person Name *' : 'Devotee Name *'}</Label>
                          <Input type="text" placeholder={config.has_death_date ? 'Enter name of the deceased' : 'Enter your name'} value={form.name} onChange={(e) => updateItemForm(poojaId, 'name', e.target.value)} className="rounded-xl p-3 border-stone-200" data-testid={`cart-name-${poojaId}`} />
                        </div>

                        {/* Death Date (only for Thilahomam) */}
                        {config.has_death_date && (
                          <>
                            <div className="flex items-center space-x-3 p-3 rounded-xl" style={{ backgroundColor: '#F5F5F4' }}>
                              <Checkbox id={`dkd-${poojaId}`} checked={form.dontKnowDate} onCheckedChange={(c) => { updateItemForm(poojaId, 'dontKnowDate', c); if (c) { updateItemForm(poojaId, 'deathDate', ''); updateItemForm(poojaId, 'nakshatra', 'Thiruvonam'); updateItemForm(poojaId, 'dontKnowNakshatra', true); } else { updateItemForm(poojaId, 'nakshatra', 'unknown'); updateItemForm(poojaId, 'dontKnowNakshatra', false); } }} data-testid={`cart-dkd-${poojaId}`} />
                              <Label htmlFor={`dkd-${poojaId}`} className="text-sm font-medium cursor-pointer">I don't know death date</Label>
                            </div>
                            {form.dontKnowDate && (
                              <div className="rounded-xl p-4 border-2" style={{ backgroundColor: '#FEF3C7', borderColor: '#FCD34D' }}>
                                <div className="text-center p-2 rounded-lg" style={{ backgroundColor: '#FDE68A' }}>
                                  <div className="text-xs font-semibold mb-1" style={{ color: '#92400E' }}>Death Nakshatram:</div>
                                  <div className="text-lg font-bold" style={{ color: '#78350F' }}>Thiruvonam</div>
                                </div>
                              </div>
                            )}
                            {!form.dontKnowDate && (
                              <div className="space-y-2">
                                <Label className="text-xs font-medium uppercase tracking-widest" style={{ color: '#57534E' }}>Death Date *</Label>
                                <Input type="date" value={form.deathDate} onChange={(e) => updateItemForm(poojaId, 'deathDate', e.target.value)} className="rounded-xl p-3 border-stone-200" data-testid={`cart-date-${poojaId}`} />
                              </div>
                            )}
                          </>
                        )}

                        {/* Death Nakshatra */}
                        {config.has_death_nakshatra && !form.dontKnowDate && (
                          <div className="space-y-2">
                            <div className="flex items-center space-x-3 p-3 rounded-xl" style={{ backgroundColor: '#F5F5F4' }}>
                              <Checkbox id={`dkn-${poojaId}`} checked={form.dontKnowNakshatra} onCheckedChange={(c) => { updateItemForm(poojaId, 'dontKnowNakshatra', c); updateItemForm(poojaId, 'nakshatra', c ? 'Thiruvonam' : 'unknown'); }} data-testid={`cart-dkn-${poojaId}`} />
                              <Label htmlFor={`dkn-${poojaId}`} className="text-sm font-medium cursor-pointer">I don't know Nakshatram</Label>
                            </div>
                            {!form.dontKnowNakshatra && (
                              <Select value={form.nakshatra} onValueChange={(v) => updateItemForm(poojaId, 'nakshatra', v)}>
                                <SelectTrigger className="w-full rounded-xl p-3 border-stone-200" data-testid={`cart-nakshatra-${poojaId}`}><SelectValue placeholder="Select Nakshatra" /></SelectTrigger>
                                <SelectContent className="max-h-[300px]">{MALAYALAM_NAKSHATRAS.map(n => <SelectItem key={n.value} value={n.value}>{n.label}</SelectItem>)}</SelectContent>
                              </Select>
                            )}
                          </div>
                        )}

                        {/* Birth Nakshatra (Ganapathi & Archana) */}
                        {(poojaId === 'ganapathi-homam' || poojaId === 'archana') && (
                          <div className="space-y-2">
                            <Label className="text-xs font-medium uppercase tracking-widest" style={{ color: '#57534E' }}>Birth Nakshatra (optional)</Label>
                            <Select value={form.birthNakshatra} onValueChange={(v) => updateItemForm(poojaId, 'birthNakshatra', v)}>
                              <SelectTrigger className="w-full rounded-xl p-3 border-stone-200"><SelectValue /></SelectTrigger>
                              <SelectContent className="max-h-[300px]">{MALAYALAM_NAKSHATRAS.map(n => <SelectItem key={n.value} value={n.value}>{n.label}</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                        )}

                        {/* Dakshina */}
                        {config.has_dakshina && (
                          <div className="space-y-2">
                            <Label className="text-xs font-medium uppercase tracking-widest" style={{ color: '#57534E' }}>Dakshina (Tip to Poojari)</Label>
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-base" style={{ color: '#78716C' }}>₹</span>
                              <Input type="number" placeholder="20" value={form.dakshina} onChange={(e) => updateItemForm(poojaId, 'dakshina', e.target.value)} className="rounded-xl p-3 pl-7 border-stone-200" data-testid={`cart-dakshina-${poojaId}`} />
                            </div>
                          </div>
                        )}

                        {/* Delivery */}
                        <div className="flex items-center space-x-3 p-3 rounded-xl" style={{ backgroundColor: '#F5F5F4' }}>
                          <Checkbox id={`del-${poojaId}`} checked={form.delivery} onCheckedChange={(c) => updateItemForm(poojaId, 'delivery', c)} data-testid={`cart-delivery-${poojaId}`} />
                          <Label htmlFor={`del-${poojaId}`} className="text-sm font-medium cursor-pointer">Receive ticket + prasadam by courier (+₹{config.delivery_charge})</Label>
                        </div>

                        {/* Item subtotal */}
                        <div className="rounded-xl p-3 flex justify-between items-center" style={{ backgroundColor: '#FEF3C7' }}>
                          <span className="text-sm font-medium" style={{ color: '#92400E' }}>Subtotal</span>
                          <span className="text-lg font-bold" style={{ color }}>₹{calculateItemSubtotal(poojaId)}</span>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>

        {/* Shared Fields */}
        <div className="bg-white shadow-lg rounded-2xl p-5 space-y-5 mb-4">
          <h3 className="text-sm font-bold uppercase tracking-widest" style={{ color: '#57534E' }}>Contact & Payment</h3>
          {/* WhatsApp */}
          <div className="space-y-2">
            <Label className="text-xs font-medium uppercase tracking-widest" style={{ color: '#57534E' }}>WhatsApp Number *</Label>
            <Input type="tel" placeholder="Enter 10-digit number" value={whatsapp} onChange={(e) => setWhatsapp(e.target.value.replace(/\D/g, '').slice(0, 10))} maxLength={10} className="rounded-xl p-3 border-stone-200" data-testid="cart-whatsapp-input" />
            {whatsapp && whatsapp.length < 10 && <p className="text-xs text-red-600">Please enter 10 digits</p>}
          </div>

          {/* Delivery Address (shared, shown if any item has delivery) */}
          {anyDelivery && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="rounded-xl p-4 border-2 space-y-3" style={{ backgroundColor: '#F0FDF4', borderColor: '#86EFAC' }}>
              <div className="text-sm font-semibold" style={{ color: '#166534' }}>Courier Address (shared for all deliveries)</div>
              <Input placeholder="Full Name" value={deliveryAddress.fullName} onChange={(e) => setDeliveryAddress({...deliveryAddress, fullName: e.target.value})} className="rounded-lg p-3" data-testid="cart-delivery-fullname" />
              <Input placeholder="Address" value={deliveryAddress.address} onChange={(e) => setDeliveryAddress({...deliveryAddress, address: e.target.value})} className="rounded-lg p-3" />
              <div className="grid grid-cols-2 gap-3">
                <Input placeholder="City" value={deliveryAddress.city} onChange={(e) => setDeliveryAddress({...deliveryAddress, city: e.target.value})} className="rounded-lg p-3" />
                <Input placeholder="District" value={deliveryAddress.district} onChange={(e) => setDeliveryAddress({...deliveryAddress, district: e.target.value})} className="rounded-lg p-3" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Input placeholder="PIN Code" type="tel" maxLength={6} value={deliveryAddress.pinCode} onChange={(e) => setDeliveryAddress({...deliveryAddress, pinCode: e.target.value.replace(/\D/g, '')})} className="rounded-lg p-3" />
                <Input placeholder="Phone" type="tel" maxLength={10} value={deliveryAddress.phoneNumber} onChange={(e) => setDeliveryAddress({...deliveryAddress, phoneNumber: e.target.value.replace(/\D/g, '')})} className="rounded-lg p-3" />
              </div>
            </motion.div>
          )}

          {/* Payment Option */}
          <div className="space-y-3">
            <Label className="text-xs font-medium uppercase tracking-widest" style={{ color: '#57534E' }}>Payment Option *</Label>
            <RadioGroup value={paymentOption} onValueChange={setPaymentOption}>
              <div className={`relative rounded-2xl p-4 border-2 cursor-pointer transition-all ${paymentOption === 'pay_early' ? 'border-green-500 bg-green-50' : 'border-stone-200'}`} onClick={() => setPaymentOption('pay_early')} data-testid="cart-pay-early-option">
                <div className="flex items-start gap-3"><RadioGroupItem value="pay_early" id="cart-pe" className="mt-1" /><div className="flex-1"><div className="flex items-center gap-2 flex-wrap"><Label htmlFor="cart-pe" className="text-base font-semibold cursor-pointer">Pay Early & Confirm</Label><span className="text-xs bg-green-600 text-white px-2 py-0.5 rounded-full font-medium">Recommended</span></div><p className="text-xs mt-1" style={{ color: '#57534E' }}>Advance payment helps confirm your booking quickly.</p></div>{paymentOption === 'pay_early' && <CheckCircle2 className="h-5 w-5 text-green-600" />}</div>
              </div>
              <div className={`relative rounded-2xl p-4 border-2 cursor-pointer transition-all ${paymentOption === 'cash_on_booking' ? 'border-amber-500 bg-amber-50' : 'border-stone-200'}`} onClick={() => setPaymentOption('cash_on_booking')} data-testid="cart-cash-option">
                <div className="flex items-start gap-3"><RadioGroupItem value="cash_on_booking" id="cart-cob" className="mt-1" /><div className="flex-1"><Label htmlFor="cart-cob" className="text-base font-semibold cursor-pointer">Cash on Booking</Label><p className="text-xs mt-1" style={{ color: '#57534E' }}>Pay after the pooja.</p></div>{paymentOption === 'cash_on_booking' && <CheckCircle2 className="h-5 w-5 text-amber-600" />}</div>
              </div>
            </RadioGroup>
          </div>

          {/* Trust */}
          <div className="rounded-xl p-4" style={{ backgroundColor: '#EFF6FF', border: '1px solid #BFDBFE' }}>
            <div className="flex items-start gap-3"><Shield className="h-4 w-4 mt-0.5" style={{ color: '#1E40AF' }} /><div className="flex-1 space-y-1"><div className="font-semibold text-xs" style={{ color: '#1E40AF' }}>Trust & Safety</div><ul className="text-xs space-y-0.5" style={{ color: '#1E3A8A' }}><li>We are a private booking service</li><li>Official temple fee is paid on your behalf</li><li>Temple receipt will be shared after booking</li></ul></div></div>
          </div>
        </div>

        {/* Price Breakdown */}
        <div className="bg-white shadow-lg rounded-2xl p-5 mb-4" data-testid="cart-price-breakdown">
          <h3 className="text-sm font-bold uppercase tracking-widest mb-4" style={{ color: '#57534E' }}>Order Summary</h3>
          <div className="space-y-3">
            {cartItems.map(poojaId => {
              const config = settings[poojaId];
              const form = itemForms[poojaId];
              if (!config || !form) return null;
              return (
                <div key={poojaId} className="rounded-xl p-3" style={{ backgroundColor: '#F5F5F4' }}>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm font-semibold" style={{ color: '#1C1917' }}>{POOJA_LABELS[poojaId] || poojaId}</span>
                    <span className="text-sm font-bold" style={{ color: POOJA_COLORS[poojaId] || '#92400E' }}>₹{calculateItemSubtotal(poojaId)}</span>
                  </div>
                  <div className="text-xs space-y-0.5" style={{ color: '#78716C' }}>
                    <div className="flex justify-between"><span>Temple Fee</span><span>₹{config.temple_fee}</span></div>
                    <div className="flex justify-between"><span>Convenience Fee</span><span>₹{config.convenience_fee}</span></div>
                    {form.delivery && <div className="flex justify-between"><span>Delivery</span><span>₹{config.delivery_charge}</span></div>}
                    {config.has_dakshina && parseInt(form.dakshina) > 0 && <div className="flex justify-between"><span>Dakshina</span><span>₹{form.dakshina}</span></div>}
                  </div>
                </div>
              );
            })}
            <div className="border-t pt-3" style={{ borderColor: '#D6D3D1' }}>
              <div className="flex justify-between items-center">
                <span className="text-xl font-semibold" style={{ color: '#1C1917' }}>Grand Total</span>
                <span className="text-2xl font-bold" style={{ color: '#C2410C' }} data-testid="cart-grand-total">₹{grandTotal}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <Button onClick={handleSubmit} disabled={submitting} className="w-full text-lg font-semibold p-6 rounded-xl shadow-lg" style={{ backgroundColor: '#25D366', color: 'white' }} data-testid="cart-submit-button">
          <CheckCircle2 className="mr-2 h-5 w-5" />{submitting ? 'Processing...' : `Confirm Booking (₹${grandTotal})`}
        </Button>

        <p className="text-xs text-center mt-4 px-4" style={{ color: '#78716C' }}>
          This is a private booking service and not an official temple authority. All booking confirmations and receipts will be shared via WhatsApp.
        </p>
      </div>

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" onClick={() => setShowConfirmDialog(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 space-y-5" onClick={(e) => e.stopPropagation()} data-testid="confirm-dialog">
            <div className="text-center">
              <h3 className="text-lg font-bold" style={{ color: '#1C1917', fontFamily: '"Cormorant Garamond", serif' }}>Confirm Your Booking</h3>
              <p className="text-xs mt-1" style={{ color: '#78716C' }}>Please enter your details to proceed</p>
            </div>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs font-medium uppercase tracking-widest" style={{ color: '#57534E' }}>Your Name *</Label>
                <Input type="text" placeholder="Enter your name" value={bookerName} onChange={(e) => setBookerName(e.target.value)} className="rounded-xl p-3 border-stone-200" data-testid="booker-name-input" autoFocus />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-medium uppercase tracking-widest" style={{ color: '#57534E' }}>Place *</Label>
                <Input type="text" placeholder="Enter your place" value={bookerPlace} onChange={(e) => setBookerPlace(e.target.value)} className="rounded-xl p-3 border-stone-200" data-testid="booker-place-input" />
              </div>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setShowConfirmDialog(false)} className="flex-1 rounded-xl">Cancel</Button>
              <Button onClick={handleFinalSubmit} disabled={submitting} className="flex-1 rounded-xl text-white font-semibold" style={{ backgroundColor: '#C2410C' }} data-testid="final-confirm-btn">
                {submitting ? 'Processing...' : 'Confirm'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
